import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, Card, Col, Image, Input, Modal, Popover, Progress, Row, Space, Table, Tag, Typography, Upload, Select, message} from "antd";
import {DownloadOutlined, InboxOutlined, ReloadOutlined, PlusOutlined, DatabaseOutlined, CheckCircleOutlined, SyncOutlined, WarningOutlined, StopOutlined, CloseOutlined, FileImageOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import JSZip from "jszip";

import "./WebPToolPage.css";

import {createJobFromFiles, downloadJobArchive, fetchJobs} from "../api/webpJobs.js";

const {Title, Paragraph, Text} = Typography;

const STATUS_META = {
	queued: {text: "In coda", color: "default"},
	processing: {text: "Elaborazione", color: "geekblue"},
	completed: {text: "Completato", color: "green"},
	error: {text: "Errore", color: "red"},
	pending: {text: "In attesa di invio", color: "default"},
	unsupported: {text: "Non convertibile", color: "volcano"}
};

const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
const MEDIA_ROOT = API_BASE.replace(/\/api$/, "");

const SUPPORTED_IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".tif", ".tiff", ".webp", ".psd", ".ai", ".pdf", ".bmp"];

const isSupportedImage = (name = "") =>
{
	const lower = name.toLowerCase();
	return SUPPORTED_IMAGE_EXTENSIONS.some(extension => lower.endsWith(extension));
};

const isHiddenPath = (path = "") =>
{
	if (!path)
		return false;

	return String(path).split(/[\\/]/).some((segment) =>
	{
		if (!segment || segment === "." || segment === "..")
			return false;

		return segment.startsWith(".");
	});
};

const formatBytes = (bytes) =>
{
	if (!bytes)
		return "0 B";

	const units = ["B", "KB", "MB", "GB"];
	const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
	const value = bytes / Math.pow(1024, power);
	return `${value.toFixed(value >= 10 || power === 0 ? 0 : 1)} ${units[power]}`;
};

function resolveMediaUrl(path)
{
	if (!path)
		return null;

	if (/^https?:\/\//i.test(path))
		return path;

	const base = MEDIA_ROOT || API_BASE;
	if (!base)
		return path;

	const separator = path.startsWith("/") ? "" : "/";
	return `${base}${separator}${path.replace(/^\//, "")}`;
}

export default function WebPToolPage()
{
	const navigate = useNavigate();
	const [messageApi, contextHolder] = message.useMessage();
	const [jobs, setJobs] = useState([]);
	const [selectedJobId, setSelectedJobId] = useState(null);
	const [fileList, setFileList] = useState([]);
	const [loadingJobs, setLoadingJobs] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [isNameModalOpen, setIsNameModalOpen] = useState(false);
	const [jobNameInput, setJobNameInput] = useState("");
	const [downloadingArchive, setDownloadingArchive] = useState(false);
	const [pendingRows, setPendingRows] = useState([]);
	const [pendingDetected, setPendingDetected] = useState(0);
	const eventSourceRef = useRef(null);
	const objectUrlsRef = useRef(new Map());
	const removedEntryIdsRef = useRef(new Set());

	const maxUploadSize = useMemo(() =>
	{
		const envValue = Number(import.meta.env.VITE_WEBP_MAX_UPLOAD_SIZE_BYTES);
		if (Number.isFinite(envValue) && envValue > 0)
			return envValue;

		return 2 * 1024 * 1024 * 1024;
	}, []);

	const maxUploadSizeLabel = useMemo(() => formatBytes(maxUploadSize), [maxUploadSize]);

	const selectedJob = useMemo(() => jobs.find(job => job.id === selectedJobId) || null, [jobs, selectedJobId]);

	const tableData = useMemo(() =>
	{
		const combined = [...pendingRows, ...(selectedJob?.files ?? [])];

		return combined.map((record) =>
		{
			const nextRecord = {...record};

			if (record.status === "unsupported")
			{
				nextRecord.thumbnail = null;
				return nextRecord;
			}

			if (record.status === "completed")
			{
				const completedPreview = resolveMediaUrl(record.outputPath) || resolveMediaUrl(record.inputPreviewPath) || record.previewUrl || objectUrlsRef.current.get(record.id) || null;
				nextRecord.thumbnail = completedPreview;
				return nextRecord;
			}

			const previewPath = resolveMediaUrl(record.inputPreviewPath);
			if (previewPath)
			{
				nextRecord.thumbnail = previewPath;
				return nextRecord;
			}

			const localPreview = record.previewUrl || objectUrlsRef.current.get(record.id);
			nextRecord.thumbnail = localPreview || null;
			return nextRecord;
		});
	}, [pendingRows, selectedJob]);

	const errorEntries = useMemo(() =>
	{
		const unique = new Map();

		const collect = (items = []) =>
		{
			items.forEach((item) =>
			{
				if (!item || (item.status !== "error" && item.status !== "unsupported"))
					return;

				const key = item.id ?? item.uid ?? item.originalName ?? item.name ?? `entry-${unique.size}`;

				if (!unique.has(key))
					unique.set(key, item);
			});
		};

		collect(pendingRows);
		collect(selectedJob?.files ?? []);

		return Array.from(unique.values());
	}, [pendingRows, selectedJob]);

	const totals = useMemo(() =>
	{
		if (pendingRows.length)
		{
			return pendingRows.reduce((accumulator, item) =>
			{
				accumulator.total += 1;

				if (item.status === "completed")
					accumulator.completed += 1;
				else if (item.status === "error" || item.status === "unsupported")
					accumulator.errors += 1;
				else
					accumulator.processing += 1;

				return accumulator;
			}, {total: 0, completed: 0, processing: 0, errors: 0});
		}

		if (!selectedJob)
			return {total: 0, completed: 0, processing: 0, errors: 0};

		const total = selectedJob.totals?.total ?? selectedJob.files?.length ?? 0;
		const completed = selectedJob.totals?.completed ?? selectedJob.files.filter(item => item.status === "completed").length;
		const errors = selectedJob.totals?.errors ?? selectedJob.files.filter(item => item.status === "error" || item.status === "unsupported").length;
		const processing = Math.max(total - completed - errors, 0);

		return {total, completed, processing, errors};
	}, [pendingRows, selectedJob]);

	const jobOptions = useMemo(() => jobs.map(job =>
	{
		const isRefsJob = job.type === "flyer-refs";
		const friendlyName = (job.name || "").trim();
		const baseLabel = friendlyName || `Job #${job.id.slice(-6)}`;
		const label = isRefsJob
			? `${baseLabel} (Refs)`
			: baseLabel;

		return {
			value: job.id,
			label,
			status: job.status,
			isRefsJob
		};
	}), [jobs]);

	const mergeJob = useCallback((job) =>
	{
		if (!job || !job.id)
			return;

		setJobs(prev =>
		{
			const map = new Map(prev.map(item => [item.id, item]));
			const existing = map.get(job.id) || {};
			map.set(job.id, {...existing, ...job});
			const next = Array.from(map.values());
			next.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
			return next;
		});
	}, []);

	const loadJobs = useCallback(async () =>
	{
		setLoadingJobs(true);

		try
		{
			const list = await fetchJobs();
			setJobs(list);
			setSelectedJobId(prev =>
			{
				if (prev && list.some(job => job.id === prev))
					return prev;
				return list[0]?.id || null;
			});
		}
		catch (error)
		{
			console.error("Impossibile caricare i job WebP", error);
			const msg = error?.response?.data?.message || "Errore durante il caricamento dei job";
			messageApi.error(msg);
		}
		finally
		{
			setLoadingJobs(false);
		}
	}, [messageApi]);

	useEffect(() =>
	{
		loadJobs();
	}, [loadJobs]);

	useEffect(() =>
	{
		if (typeof window === "undefined")
			return;

		const API = import.meta.env.VITE_API_URL || "";
		const url = `${API}/api/events?topic=webp-jobs`;
		const source = new EventSource(url, {withCredentials: true});

		eventSourceRef.current = source;

		const handleCreated = (event) =>
		{
			try
			{
				const payload = JSON.parse(event.data);
				mergeJob(payload);
				setSelectedJobId(prev => prev || payload.id);
			}
			catch (err)
			{
				console.error("Errore parsing SSE job.created", err);
			}
		};

		const handleUpdated = (event) =>
		{
			try
			{
				const payload = JSON.parse(event.data);
				mergeJob(payload);
			}
			catch (err)
			{
				console.error("Errore parsing SSE job.update", err);
			}
		};

		source.addEventListener("job.created", handleCreated);
		source.addEventListener("job.update", handleUpdated);
		source.onerror = (event) =>
		{
			console.error("Connessione SSE webp interrotta", event);
		};

		return () =>
		{
			source.removeEventListener("job.created", handleCreated);
			source.removeEventListener("job.update", handleUpdated);
			source.close();
			eventSourceRef.current = null;
		};
	}, [mergeJob]);

	const revokeObjectUrl = useCallback((uid, options = {}) =>
	{
		if (typeof window === "undefined" || typeof URL === "undefined" || typeof URL.revokeObjectURL !== "function")
			return;

		const {includeNested = false} = options;
		const map = objectUrlsRef.current;

		if (includeNested)
		{
			const prefix = `${uid}:`;
			Array.from(map.keys()).forEach((key) =>
			{
				if (key === uid || key.startsWith(prefix))
				{
					const storedUrl = map.get(key);
					if (storedUrl)
						URL.revokeObjectURL(storedUrl);
					map.delete(key);
				}
			});
			return;
		}

		const url = map.get(uid);
		if (!url)
			return;

		URL.revokeObjectURL(url);
		map.delete(uid);
	}, []);

	const releaseAllObjectUrls = useCallback(() =>
	{
		if (typeof window === "undefined" || typeof URL === "undefined" || typeof URL.revokeObjectURL !== "function")
			return;

		const map = objectUrlsRef.current;
		map.forEach((url) =>
		{
			URL.revokeObjectURL(url);
		});
		map.clear();
	}, []);

	const handleUploadSubmit = useCallback(async (jobName = "") =>
	{
		if (!pendingRows.length)
		{
			messageApi.warning("Aggiungi almeno un file immagine o un archivio");
			return;
		}

		const rowStatuses = new Map();
		const zipSelections = new Map();
		for (const row of pendingRows)
		{
			if (!row?.id)
				continue;

			rowStatuses.set(row.id, row.status);

			if (row.status !== "pending")
				continue;

			if (!row.id.includes(":"))
				continue;

			const separatorIndex = row.id.indexOf(":");
			const rootUid = row.id.slice(0, separatorIndex);
			const relativePath = row.id.slice(separatorIndex + 1);
			if (!relativePath)
				continue;

			let paths = zipSelections.get(rootUid);
			if (!paths)
			{
				paths = new Set();
				zipSelections.set(rootUid, paths);
			}
			paths.add(relativePath);
		}

		setSubmitting(true);

		try
		{
			const normalizedName = (jobName || "").trim() || null;
			const files = [];
			const zipCache = new Map();

			for (const fileItem of fileList)
			{
				const rawFile = fileItem.originFileObj || fileItem;
				if (!rawFile)
					continue;

				const baseName = rawFile.name || fileItem.name || "File";

				if (baseName.endsWith(".zip") && rawFile instanceof Blob)
				{
					const selectedPaths = zipSelections.get(fileItem.uid);
					if (!selectedPaths || !selectedPaths.size)
						continue;

					let zip = zipCache.get(fileItem.uid);
					if (!zip)
					{
						try
						{
							zip = await JSZip.loadAsync(rawFile);
							zipCache.set(fileItem.uid, zip);
						}
						catch (error)
						{
							console.error("Errore lettura archivio ZIP", error);
							messageApi.error(`Impossibile leggere l'archivio ${baseName}`);
							continue;
						}
					}

					for (const relativePath of selectedPaths)
					{
						if (!relativePath || isHiddenPath(relativePath))
							continue;

						const entryId = `${fileItem.uid}:${relativePath}`;
						if (rowStatuses.get(entryId) !== "pending")
							continue;

						const entry = zip.file(relativePath);
						if (!entry)
							continue;

						try
						{
							const blob = await entry.async("blob");
							const parts = relativePath.split("/");
							const leafName = parts[parts.length - 1] || parts[parts.length - 2] || baseName;
							const fileOptions = {type: blob.type || undefined, lastModified: Date.now()};
							const extracted = new File([blob], leafName, fileOptions);

							try
							{
								Object.defineProperty(extracted, "webkitRelativePath", {value: relativePath});
							}
							catch
							{
								// Il percorso relativo è facoltativo
							}

							files.push(extracted);
						}
						catch (entryError)
						{
							console.error("Impossibile estrarre l'entry ZIP", entryError);
						}
					}

					continue;
				}

				const relativePath = rawFile.webkitRelativePath || rawFile.name || fileItem.name || "";
				if (isHiddenPath(relativePath))
					continue;

				if (rowStatuses.get(fileItem.uid) !== "pending")
					continue;

				files.push(rawFile);
			}

			if (!files.length)
			{
				messageApi.warning("Nessun file valido da inviare");
				return;
			}

			const job = await createJobFromFiles(files, {name: normalizedName});
			mergeJob(job);
			setSelectedJobId(job?.id || null);
			setFileList([]);
			releaseAllObjectUrls();
			removedEntryIdsRef.current.clear();
			messageApi.success("Job di conversione avviato");
		}
		catch (error)
		{
			console.error("Errore creazione job WebP", error);
			const msg = error?.response?.data?.message || "Impossibile creare il job";
			messageApi.error(msg);
		}
		finally
		{
			setSubmitting(false);
		}
	}, [fileList, mergeJob, messageApi, pendingRows, releaseAllObjectUrls]);
	const handleClearPending = useCallback(() =>
	{
		setFileList([]);
		releaseAllObjectUrls();
		removedEntryIdsRef.current.clear();
	}, [releaseAllObjectUrls]);

	const selectedFilesCount = pendingDetected;

	const openNameModal = useCallback(() =>
	{
		if (!pendingRows.length)
		{
			messageApi.warning("Aggiungi almeno un file immagine o un archivio");
			return;
		}

		setJobNameInput("");
		setIsNameModalOpen(true);
	}, [messageApi, pendingRows.length]);

	const handleConfirmName = useCallback(async () =>
	{
		const trimmed = jobNameInput.trim();
		if (!trimmed)
		{
			messageApi.warning("Inserisci un nome per il job");
			return;
		}

		setIsNameModalOpen(false);
		await handleUploadSubmit(trimmed);
	}, [handleUploadSubmit, jobNameInput, messageApi]);

	const handleCancelName = useCallback(() =>
	{
		setIsNameModalOpen(false);
		setJobNameInput("");
	}, []);

	useEffect(() =>
	{
		if (typeof window === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function")
			return undefined;

		const isBlobAvailable = typeof Blob !== "undefined";
		const map = objectUrlsRef.current;
		const nextUids = new Set(fileList.map(item => item.uid));

		for (const fileItem of fileList)
		{
			const uid = fileItem.uid;
			if (!uid || map.has(uid))
				continue;

			const rawFile = fileItem.originFileObj || fileItem;
			if (!rawFile || !isBlobAvailable || !(rawFile instanceof Blob))
				continue;

			try
			{
				const url = URL.createObjectURL(rawFile);
				map.set(uid, url);
			}
			catch (error)
			{
				console.error("Impossibile creare object URL per il file", error);
			}
		}

		const processedRoots = new Set();
		Array.from(map.keys()).forEach((key) =>
		{
			const [rootUid] = key.split(":");
			if (!nextUids.has(rootUid) && !processedRoots.has(rootUid))
			{
				processedRoots.add(rootUid);
				revokeObjectUrl(rootUid, {includeNested: true});
			}
		});

		return undefined;
	}, [fileList, revokeObjectUrl]);

	useEffect(() =>
	{
		let cancelled = false;

		const buildPendingRows = async () =>
		{
			if (!fileList.length)
			{
				if (!cancelled)
				{
					setPendingRows([]);
					setPendingDetected(0);
					removedEntryIdsRef.current.clear();
				}
				return;
			}

			const rows = [];
			let detectedCount = 0;
			const canUseObjectUrl = typeof window !== "undefined" && typeof URL !== "undefined" && typeof URL.createObjectURL === "function" && typeof Blob !== "undefined";
			const urlMap = objectUrlsRef.current;

			for (const fileItem of fileList)
			{
				const rawFile = fileItem.originFileObj || fileItem;
				if (!rawFile)
					continue;

				const baseName = rawFile.name || fileItem.name || "File";

				if (/\.zip$/i.test(baseName))
				{
					try
					{
						const zip = await JSZip.loadAsync(rawFile);
						const entries = [];
						zip.forEach((relativePath, zipEntry) =>
						{
							if (!zipEntry.dir)
								entries.push({relativePath, zipEntry});
						});

						for (const {relativePath, zipEntry} of entries)
						{
							const entryId = `${fileItem.uid}:${relativePath}`;
							if (removedEntryIdsRef.current.has(entryId))
							{
								revokeObjectUrl(entryId, {includeNested: true});
								continue;
							}
							if (isHiddenPath(relativePath))
							{
								revokeObjectUrl(entryId, {includeNested: true});
								continue;
							}

							detectedCount += 1;
							const supported = isSupportedImage(relativePath);
							let previewUrl = null;

							if (supported && canUseObjectUrl)
							{
								previewUrl = urlMap.get(entryId) || null;

								if (!previewUrl)
								{
									try
									{
										const blob = await zipEntry.async("blob");
										previewUrl = URL.createObjectURL(blob);
										urlMap.set(entryId, previewUrl);
									}
									catch (error)
									{
										console.error("Impossibile generare anteprima per l'entry ZIP", error);
									}
								}
							}

							rows.push({
								id: entryId,
								originalName: `${baseName} › ${relativePath}`,
								size: zipEntry._data?.uncompressedSize ?? zipEntry._data?.compressedSize ?? 0,
								status: supported ? "pending" : "unsupported",
								progress: 0,
								previewUrl: supported ? previewUrl : null,
								...(supported ? {} : {error: "Formato non supportato per la conversione in WebP"})
							});
						}
					}
					catch (error)
					{
						console.error("Errore lettura archivio ZIP", error);
						if (!cancelled)
							messageApi.error(`Impossibile leggere l'archivio ${baseName}`);
					}

					continue;
				}

				const displayName = rawFile.webkitRelativePath || baseName;
				if (isHiddenPath(displayName))
				{
					revokeObjectUrl(fileItem.uid, {includeNested: true});
					continue;
				}

				detectedCount += 1;
				const supported = isSupportedImage(displayName);
				let previewUrl = null;

				if (supported && canUseObjectUrl)
				{
					previewUrl = urlMap.get(fileItem.uid) || null;

					if (!previewUrl && rawFile instanceof Blob)
					{
						try
						{
							previewUrl = URL.createObjectURL(rawFile);
							urlMap.set(fileItem.uid, previewUrl);
						}
						catch (error)
						{
							console.error("Impossibile generare anteprima per il file", error);
						}
					}
				}

				rows.push({
					id: fileItem.uid,
					originalName: displayName,
					size: rawFile.size ?? fileItem.size ?? 0,
					status: supported ? "pending" : "unsupported",
					progress: 0,
					previewUrl: supported ? previewUrl : null,
					...(supported ? {} : {error: "Formato non supportato per la conversione in WebP"})
				});
			}

			if (!cancelled)
			{
				setPendingRows(prevRows =>
				{
					const previousIds = new Set(prevRows.map(row => row.id));
					const nextIds = new Set(rows.map(row => row.id));
					previousIds.forEach((id) =>
					{
						if (!nextIds.has(id))
							revokeObjectUrl(id);
					});
					return rows;
				});
				setPendingDetected(detectedCount);
			}
		};

		buildPendingRows();

		return () =>
		{
			cancelled = true;
		};
	}, [fileList, messageApi, revokeObjectUrl]);

	useEffect(() => () =>
	{
		releaseAllObjectUrls();
	}, [releaseAllObjectUrls]);

	const draggerProps = useMemo(() => ({
		name: "file",
		multiple: true,
		directory: true,
		beforeUpload: () => false,
		showUploadList: false,
		fileList,
		onChange: info => setFileList(info.fileList),
		onRemove: file =>
		{
			setFileList(prev => prev.filter(item => item.uid !== file.uid));
			revokeObjectUrl(file.uid, {includeNested: true});
		}
	}), [fileList, revokeObjectUrl]);

	const handleRemovePendingRow = useCallback((rowId) =>
	{
		if (!rowId)
			return;

		setPendingRows(prevRows =>
		{
			const nextRows = prevRows.filter(row => row.id !== rowId);

			if (nextRows.length !== prevRows.length)
				setPendingDetected(nextRows.length);

			return nextRows;
		});

		if (rowId.includes(":"))
		{
			removedEntryIdsRef.current.add(rowId);
		}
		else
		{
			const removedRoot = `${rowId}:`;
			Array.from(removedEntryIdsRef.current).forEach((entryId) =>
			{
				if (entryId === rowId || entryId.startsWith(removedRoot))
					removedEntryIdsRef.current.delete(entryId);
			});
			setFileList(prev => prev.filter(item => item.uid !== rowId));
		}

		revokeObjectUrl(rowId, {includeNested: true});
	}, [revokeObjectUrl, setFileList]);

	useEffect(() =>
	{
		const validRoots = new Set(fileList.map(item => item.uid));
		const removedSet = removedEntryIdsRef.current;

		Array.from(removedSet).forEach((entryId) =>
		{
			const [rootUid] = `${entryId}`.split(":");
			if (!validRoots.has(rootUid))
				removedSet.delete(entryId);
		});
	}, [fileList]);

	const pendingRowIds = useMemo(() => new Set(pendingRows.map(row => row.id)), [pendingRows]);


	const columns = useMemo(() => ([
		{
			title: "Anteprima",
			dataIndex: "thumbnail",
			key: "thumbnail",
			width: 120,
			render: (_, record) =>
			{
				if (record.status === "unsupported")
				{
					return (
						<div className="webp-preview-thumb webp-preview-thumb--unsupported" aria-hidden="true">
							<StopOutlined className="webp-preview-thumb__icon"/>
						</div>
					);
				}

				if (!record.thumbnail)
				{
					return <div className="webp-preview-thumb webp-preview-thumb--empty" aria-hidden="true"/>;
				}

				return (
					<div className="webp-preview-thumb">
						<Image
							src={record.thumbnail}
							alt={`Anteprima di ${record.originalName}`}
							width={72}
							height={72}
							className="webp-preview-thumb__image"
							preview={{mask: <div className="webp-preview-thumb__mask">Apri</div>}}
						/>
					</div>
				);
			}
		},
		{
			title: "File",
			dataIndex: "originalName",
			key: "originalName",
			render: (_, record) => (
				<Space direction="vertical" size={0}>
					<Text strong>{record.originalName}</Text>
					<Text type="secondary">{formatBytes(record.size)}</Text>
				</Space>
			)
		},
		{
			title: "Stato",
			dataIndex: "status",
			key: "status",
			width: 160,
			render: (_, record) =>
			{
				const meta = STATUS_META[record.status] || {text: record.status, color: "default"};
				return <Tag color={meta.color}>{meta.text}</Tag>;
			}
		},
		{
			title: "Avanzamento",
			dataIndex: "progress",
			key: "progress",
			width: 220,
			render: (_, record) =>
			{
				const percent = Math.round(record.progress ?? 0);
				const status = record.status === "completed" ? "success" : ((record.status === "error" || record.status === "unsupported") ? "exception" : "active");
				return <Progress percent={percent} size="small" status={status}/>;
			}
		},
		{
			title: "Output",
			key: "output",
			width: 220,
			render: (_, record) =>
			{
				if (record.status !== "completed" || !record.outputPath)
					return <Text type="secondary">—</Text>;

				const url = resolveMediaUrl(record.outputPath);
				return (
					<Button type="link" size="small" href={url} target="_blank" icon={<DownloadOutlined/>}>
						Scarica
					</Button>
				);
			}
		},
		{
			title: "Messaggi",
			key: "message",
			render: (_, record) =>
			{
				if (record.error)
					return <Text type="danger">{record.error}</Text>;

				return <Text type="secondary">—</Text>;
			}
		},
		{
			title: "Azioni",
			key: "actions",
			width: 80,
			align: "center",
			render: (_, record) =>
			{
				const isPendingRow = pendingRowIds.has(record.id);
				const canRemove = isPendingRow && (record.status === "pending" || record.status === "unsupported");

				if (!canRemove)
					return <Text type="secondary">—</Text>;

				return (
					<Button
						type="text"
						size="small"
						icon={<CloseOutlined/>}
						aria-label={`Rimuovi ${record.originalName}`}
						onClick={() => handleRemovePendingRow(record.id)}
					/>
				);
			}
		}
	]), [handleRemovePendingRow, pendingRowIds]);


	const errorPopoverContent = useMemo(() =>
	{
		if (!errorEntries.length)
			return <Text type="secondary">Nessun dettaglio errore disponibile</Text>;

		return (
			<Space direction="vertical" size={12} style={{maxWidth: 280}}>
				<ul style={{paddingLeft: 15}}>
					{errorEntries.map((item, index) =>
					{
						const key = item.id ?? item.uid ?? `${item.originalName || item.name || index}`;
						const name = item.originalName || item.name || item.fileName || item.path || `File #${index + 1}`;
						const messageText = item.error || item.message || (item.status === "unsupported" ? "Formato non supportato" : "Errore sconosciuto");

						return (
							<li key={key}>
								<Text strong style={{margin: 0}}>{name}</Text>
								{/*{messageText && <Text type="secondary" style={{margin: 0}}>{messageText}</Text>}*/}
							</li>
						);
					})}
				</ul>
			</Space>
		);
	}, [errorEntries]);

	const currentStatusMeta = STATUS_META[selectedJob?.status] || null;
	const errorButtonAriaLabel = errorEntries.length ? `Mostra dettagli per ${errorEntries.length} file in errore` : "Nessun errore registrato";
	const canDownloadArchive = selectedJob?.status === "completed" && !!selectedJobId;

	const handleDownloadArchive = useCallback(async () =>
	{
		if (!selectedJobId)
		{
			messageApi.warning("Seleziona un job completato");
			return;
		}

		setDownloadingArchive(true);

		try
		{
			const {data, filename} = await downloadJobArchive(selectedJobId);
			const blob = data instanceof Blob ? data : new Blob([data], {type: "application/zip"});
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename || `job-${selectedJobId}.zip`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		}
		catch (error)
		{
			console.error("Errore download archivio job", error);
			try
			{
				const blob = error?.response?.data;
				if (blob instanceof Blob)
				{
					const text = await blob.text();
					const parsed = JSON.parse(text);
					if (parsed?.message)
					{
						messageApi.error(parsed.message);
						return;
					}
				}
			}
			catch
			{
				// Ignora parsing errori e usa fallback
			}
			messageApi.error("Impossibile scaricare l'archivio");
		}
		finally
		{
			setDownloadingArchive(false);
		}
	}, [messageApi, selectedJobId]);

	return (
		<Space direction="vertical" size={24} style={{width: "100%"}}>
			{contextHolder}
			<Modal
				title="Nome del processo"
				open={isNameModalOpen}
				onOk={handleConfirmName}
				onCancel={handleCancelName}
				okText="Avvia"
				cancelText="Annulla"
				confirmLoading={submitting}
				centered
			>
				<Space direction="vertical" size={8} style={{width: "100%"}}>
					<Text type="secondary">Assegna un nome descrittivo per ritrovare il job nella lista.</Text>
					<Input
						placeholder="Es. Catalogo primavera, Prodotto X"
						value={jobNameInput}
						onChange={event => setJobNameInput(event.target.value)}
						onPressEnter={handleConfirmName}
						autoFocus
					/>
				</Space>
			</Modal>
			<div>
				<Title level={3} style={{marginBottom: 0}}>WebP Tool</Title>
				<Text type="secondary">Gestisci conversioni batch in WebP con monitoraggio in tempo reale.</Text>
			</div>

			<Row gutter={[16]}>
				<Col span={12}>
					<Card className="card-elev" styles={{body: {padding: 24}}}>
						<Space direction="vertical" size={15} style={{width: "100%"}}>
							<Paragraph style={{marginBottom: 0}}>
								<b>Trascina qui immagini singole, cartelle o archivi ZIP.</b>
							</Paragraph>
							<Text type="secondary">Dimensione massima per file o archivi: {maxUploadSizeLabel} (configurabile via ambiente).</Text>
							<Upload.Dragger {...draggerProps} disabled={submitting}>
								<p className="ant-upload-drag-icon">
									<InboxOutlined/>
								</p>
								<p className="ant-upload-text">Trascina file o cartelle oppure clicca per selezionare</p>
								<p className="ant-upload-hint">Formati supportati: PNG, JPG, TIFF, WebP e ZIP. Puoi selezionare cartelle con immagini compatibili.</p>
							</Upload.Dragger>
							<Space align="center" size={16} wrap>
								<div className={`webp-upload-feedback${!selectedFilesCount ? " webp-upload-feedback--empty" : ""}`} aria-live="polite">
									<span className="webp-upload-feedback__icon">
										<InboxOutlined/>
									</span>
									<span className="webp-upload-feedback__text">
										{selectedFilesCount ? `${selectedFilesCount} file selezionati` : "Nessun file selezionato"}
									</span>
								</div>

								<Button type="primary" icon={<PlusOutlined/>} disabled={!pendingRows.length} loading={submitting} onClick={openNameModal}>
									Avvia conversione
								</Button>

								<Button icon={<ReloadOutlined/>} disabled={!fileList.length} onClick={handleClearPending}>Svuota selezione</Button>
							</Space>
						</Space>
					</Card>
				</Col>

				<Col span={12}>
					<Row gutter={[12, 12]}>
						<Col span={24}>
							<Card className="card-elev" styles={{body: {padding: 24}}}>
								<Space direction="vertical" size={16} style={{width: "100%"}}>
									<Space wrap align="center" style={{width: "100%", justifyContent: "space-between"}}>
										<Space size={12}>
											<Select
												placeholder="Seleziona un job"
												style={{minWidth: 220}}
												options={jobOptions}
												value={selectedJobId}
												onChange={value => setSelectedJobId(value)}
											/>
											{currentStatusMeta && <Tag color={currentStatusMeta.color}>{currentStatusMeta.text}</Tag>}
											{selectedJob?.type === "flyer-refs" && (
												<Tag icon={<FileImageOutlined/>} color="blue">Referenze</Tag>
											)}
											{selectedJob?.flyerId && (
												<Button
													size="small"
													type="link"
													onClick={() => navigate(`/flyermanage/${selectedJob.flyerId}`)}
												>
													Vai al Volantino
												</Button>
											)}
										</Space>
										{canDownloadArchive ? (
											<Button
												icon={<DownloadOutlined/>}
												type="primary"
												onClick={handleDownloadArchive}
												loading={downloadingArchive}
											>
												Download ZIP
											</Button>
										) : (
											<Button icon={<ReloadOutlined/>} onClick={loadJobs} loading={loadingJobs}>Aggiorna</Button>
										)}
									</Space>
									<Text type="secondary">I job restano disponibili per riprendere lo stato anche dopo la navigazione.</Text>
								</Space>
							</Card>
						</Col>
						<Col span={12}>
							<Card className="card-elev webp-metric-card webp-metric-card--total" variant={false}>
								<span className="webp-metric-card__icon">
									<DatabaseOutlined/>
								</span>
								<div className="webp-metric-card__content">
									<span className="webp-metric-card__label">Totale file</span>
									<span className="webp-metric-card__value">{totals.total}</span>
								</div>
							</Card>
						</Col>
						<Col span={12}>
							<Card className="card-elev webp-metric-card webp-metric-card--completed" variant={false}>
								<span className="webp-metric-card__icon">
									<CheckCircleOutlined/>
								</span>
								<div className="webp-metric-card__content">
									<span className="webp-metric-card__label">Completati</span>
									<span className="webp-metric-card__value">{totals.completed}</span>
								</div>
							</Card>
						</Col>
						<Col span={12}>
							<Card className="card-elev webp-metric-card webp-metric-card--processing" variant={false}>
								<span className="webp-metric-card__icon">
									<SyncOutlined/>
								</span>
								<div className="webp-metric-card__content">
									<span className="webp-metric-card__label">In elaborazione</span>
									<span className="webp-metric-card__value">{totals.processing}</span>
								</div>
							</Card>
						</Col>
						<Col span={12}>
							<Card className="card-elev webp-metric-card webp-metric-card--errors" variant={false}>
								<span className="webp-metric-card__icon">
									<Popover
										placement="left"
										trigger={["click", "hover", "focus"]}
										content={errorPopoverContent}
									>
										<Button
											type="text"
											icon={<WarningOutlined/>}
											aria-label={errorButtonAriaLabel}
											style={{
												padding: 0,
												width: "100%",
												height: "100%",
												display: "inline-flex",
												alignItems: "center",
												justifyContent: "center",
												color: "inherit"
											}}
										/>
									</Popover>
								</span>
								<div className="webp-metric-card__content">
									<span className="webp-metric-card__label">Errori</span>
									<span className="webp-metric-card__value">{totals.errors}</span>
								</div>
							</Card>
						</Col>
					</Row>
				</Col>
			</Row>

			<Card className="card-elev" styles={{body: {padding: 0}}}>
				<Table
					rowKey="id"
					columns={columns}
					dataSource={tableData}
					pagination={{pageSize: 8, hideOnSinglePage: true}}
					locale={{emptyText: selectedJob ? "Nessun file trovato per questo job" : "Crea un job per visualizzare lo stato"}}
					loading={loadingJobs && !selectedJob}
				/>
			</Card>
		</Space>
	);
}
